import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Platform } from "react-native";

import {
  getGetPrescriptionParseStatusQueryKey,
  useGetPrescriptionParseStatus,
  useParsePrescriptionImage,
} from "@/api/generated";
import {
  DosespotMedication,
  PrescriptionImageParseResponse,
  PrescriptionImageParseResponseStatus,
} from "@/api/generated/models";

export enum PrescriptionAnalysisStatus {
  Success = "success",
  Unreadable = "unreadable",
  NoMatch = "no_match",
}

export type PrescriptionAnalysisResult =
  | {
      status: PrescriptionAnalysisStatus.Success;
      medication: DosespotMedication;
      medication_name: string;
      dosage: string;
      directions: string;
      refills?: string;
    }
  | {
      status: PrescriptionAnalysisStatus.Unreadable;
      medications: DosespotMedication[];
    }
  | { status: PrescriptionAnalysisStatus.NoMatch; error?: string };

const POLL_INTERVAL_MS = 2000;

const mapToResult = (
  data: PrescriptionImageParseResponse,
): PrescriptionAnalysisResult => {
  if (data.status === PrescriptionImageParseResponseStatus.failed) {
    return {
      status: PrescriptionAnalysisStatus.NoMatch,
      error: data.error ?? undefined,
    };
  }

  const medications = data.medications ?? [];

  if (medications.length === 0) {
    return { status: PrescriptionAnalysisStatus.NoMatch };
  }

  if (medications.length === 1) {
    const med = medications[0];
    const details = med.prescription_details;
    return {
      status: PrescriptionAnalysisStatus.Success,
      medication: med,
      medication_name: details.display_name ?? details.generic_drug_name ?? "",
      dosage: details.strength ?? "",
      directions: details.directions ?? "",
      refills: details.refills ?? undefined,
    };
  }

  return { status: PrescriptionAnalysisStatus.Unreadable, medications };
};

export const usePrescriptionAnalysis = () => {
  const queryClient = useQueryClient();
  const {
    mutate: postImage,
    isPending: isUploading,
    isSuccess: uploadSucceeded,
    isError: uploadFailed,
    reset: resetUpload,
  } = useParsePrescriptionImage({
    mutation: { meta: { suppressErrorToast: true } },
  });

  const {
    mutate: fetchImageBlob,
    isError: imageFetchFailed,
    reset: resetFetch,
  } = useMutation({
    mutationFn: (imageUri: string) => fetch(imageUri).then((r) => r.blob()),
    onSuccess: (image) => postImage({ data: { image } }),
    meta: { suppressErrorToast: true },
  });

  const { data: parseStatus, isError: parseFailed } =
    useGetPrescriptionParseStatus({
      query: {
        enabled: uploadSucceeded,
        refetchInterval: (query) =>
          query.state.data?.status ===
          PrescriptionImageParseResponseStatus.in_progress
            ? POLL_INTERVAL_MS
            : false,
        meta: { suppressErrorToast: true },
      },
    });

  const result = useMemo((): PrescriptionAnalysisResult | null => {
    if (uploadFailed || imageFetchFailed || parseFailed)
      return { status: PrescriptionAnalysisStatus.NoMatch };
    if (!uploadSucceeded || !parseStatus) return null;
    if (
      parseStatus.status === PrescriptionImageParseResponseStatus.in_progress
    ) {
      return null;
    }
    return mapToResult(parseStatus);
  }, [
    uploadFailed,
    imageFetchFailed,
    uploadSucceeded,
    parseStatus,
    parseFailed,
  ]);

  const analyze = useCallback(
    (imageUri: string) => {
      queryClient.removeQueries({
        queryKey: getGetPrescriptionParseStatusQueryKey(),
      });
      if (Platform.OS === "web") {
        fetchImageBlob(imageUri);
      } else {
        const image = {
          uri: imageUri,
          type: "image/jpeg",
          name: "prescription.jpg",
        } as unknown as Blob;
        postImage({ data: { image } });
      }
    },
    [fetchImageBlob, postImage, queryClient],
  );

  const resetAll = useCallback(() => {
    resetFetch();
    resetUpload();
  }, [resetFetch, resetUpload]);

  return {
    analyze,
    resetUpload: resetAll,
    result,
    isPending: isUploading || (uploadSucceeded && result === null),
  };
};
